/** The orchestrator's conversation sharing endpoints, validated at the boundary. */
import { z } from 'zod';
import { apiRequest } from '../../../lib/apiClient.js';
import { agentBaseUrl } from '../../../lib/agentApi.js';

const personSchema = z
  .object({ userId: z.string(), name: z.string().nullable(), email: z.string() })
  .passthrough();
export type SharePerson = z.infer<typeof personSchema>;

const shareDetailSchema = z
  .object({
    shareId: z.string(),
    sharedUpTo: z.string(),
    /** Questions the owner asked after the snapshot; absent on older servers. */
    questionsSince: z.number().optional(),
    recipients: z.array(
      personSchema.extend({ addedAt: z.string(), opened: z.boolean(), continued: z.boolean() }),
    ),
  })
  .passthrough();
export type ShareDetail = z.infer<typeof shareDetailSchema>;

const shareResponseSchema = z.object({ share: shareDetailSchema.nullable() }).passthrough();
const peopleResponseSchema = z.object({ people: z.array(personSchema) }).passthrough();

const sharePath = (threadId: string) => `/api/agent/threads/${encodeURIComponent(threadId)}/share`;
const agent = { baseUrl: agentBaseUrl };

/** The conversation's share, or null when it isn't shared. */
export const getShare = async (threadId: string): Promise<ShareDetail | null> =>
  (
    await apiRequest<z.infer<typeof shareResponseSchema>>(
      sharePath(threadId),
      agent,
      shareResponseSchema,
    )
  ).share;

/** Share with these people (adds to an existing share). */
export const shareConversation = (
  threadId: string,
  body: { userIds: string[]; note?: string },
): Promise<unknown> => apiRequest(sharePath(threadId), { ...agent, method: 'POST', body });

/** Move the share to the conversation as it is now. */
export const updateShareToLatest = (threadId: string): Promise<unknown> =>
  apiRequest(sharePath(threadId), { ...agent, method: 'PUT' });

export const stopSharing = (threadId: string): Promise<void> =>
  apiRequest<void>(sharePath(threadId), { ...agent, method: 'DELETE' });

export const removeShareRecipient = (threadId: string, userId: string): Promise<void> =>
  apiRequest<void>(`${sharePath(threadId)}/recipients/${encodeURIComponent(userId)}`, {
    ...agent,
    method: 'DELETE',
  });

/** People the caller can share with, matching a name or email. */
export const searchSharePeople = async (q: string): Promise<SharePerson[]> =>
  (
    await apiRequest<z.infer<typeof peopleResponseSchema>>(
      `/api/agent/share-recipients?q=${encodeURIComponent(q)}`,
      agent,
      peopleResponseSchema,
    )
  ).people;

const sharedConversationSchema = z
  .object({
    shareId: z.string(),
    title: z.string(),
    note: z.string().nullable(),
    sharedBy: personSchema,
    sharedAt: z.string(),
    unread: z.boolean(),
  })
  .passthrough();
export type SharedConversation = z.infer<typeof sharedConversationSchema>;

const sharedWithMeSchema = z
  .object({ unreadCount: z.number(), shares: z.array(sharedConversationSchema) })
  .passthrough();
export type SharedWithMe = z.infer<typeof sharedWithMeSchema>;

const sharedReadSchema = z
  .object({
    share: sharedConversationSchema,
    messages: z.array(
      z
        .object({
          id: z.string(),
          role: z.enum(['user', 'assistant']),
          content: z.string(),
          toolMarkers: z
            .array(
              z
                .object({ label: z.string(), detail: z.string().nullish(), afterChar: z.number() })
                .passthrough(),
            )
            .optional(),
        })
        .passthrough(),
    ),
  })
  .passthrough();
export type SharedRead = z.infer<typeof sharedReadSchema>;

const continueSchema = z.object({ conversationId: z.string() }).passthrough();

/** What others have shared with the caller, with the unread count for the badge. */
export const getSharedWithMe = (): Promise<SharedWithMe> =>
  apiRequest<SharedWithMe>('/api/agent/shared', agent, sharedWithMeSchema);

/** A shared conversation as of when it was shared. Reading it marks it opened. */
export const readSharedConversation = (shareId: string): Promise<SharedRead> =>
  apiRequest<SharedRead>(
    `/api/agent/shared/${encodeURIComponent(shareId)}/messages`,
    agent,
    sharedReadSchema,
  );

/** Copy the shared conversation into the caller's own chats; the same copy on repeat. */
export const continueSharedConversation = async (shareId: string): Promise<string> =>
  (
    await apiRequest<z.infer<typeof continueSchema>>(
      `/api/agent/shared/${encodeURIComponent(shareId)}/continue`,
      { ...agent, method: 'POST' },
      continueSchema,
    )
  ).conversationId;

/** Take a share off the caller's list until the sender updates it. */
export const hideSharedConversation = (shareId: string): Promise<void> =>
  apiRequest<void>(`/api/agent/shared/${encodeURIComponent(shareId)}/hide`, {
    ...agent,
    method: 'POST',
  });
